import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Edit, Email } from '@mui/icons-material';
import { useCompanyEmailTemplates } from '@/lib/query/hooks/useCompanies';
import { EmailTemplateEditDialog } from './EmailTemplateEditDialog';

interface EmailTemplateViewDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
}

export function EmailTemplateViewDialog({
  open,
  onClose,
  companyId,
}: EmailTemplateViewDialogProps) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const { data: templates, isLoading, error } = useCompanyEmailTemplates(companyId);

  const handleOpenEditDialog = () => {
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
  };

  return (
    <>
      <Dialog open={open && !editDialogOpen} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Email />
          メールテンプレート
        </DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">メールテンプレートの取得に失敗しました。</Alert>
          ) : templates ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              {/* 招待メッセージ */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  招待メッセージ（新規ユーザー登録時）
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    件名
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                    {templates.invitation.subject}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    本文
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      maxHeight: 300,
                      overflow: 'auto',
                    }}
                  >
                    <Typography
                      variant="body2"
                      component="div"
                      dangerouslySetInnerHTML={{ __html: templates.invitation.body }}
                    />
                  </Box>
                </Box>
              </Box>

              <Divider />

              {/* 検証メッセージ */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  検証メッセージ（パスワードリセット時）
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    件名
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                    {templates.verification.subject}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    本文
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      maxHeight: 200,
                      overflow: 'auto',
                    }}
                  >
                    <Typography
                      variant="body2"
                      component="div"
                      dangerouslySetInnerHTML={{ __html: templates.verification.body }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* プレースホルダー説明 */}
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>プレースホルダーについて:</strong>
                </Typography>
                <Typography variant="body2">
                  {'・{username} - ユーザーのメールアドレスに置換されます（招待メールのみ）'}
                </Typography>
                <Typography variant="body2">
                  {
                    '・{####} - 仮パスワード（招待メール）または確認コード（検証メール）に置換されます'
                  }
                </Typography>
              </Alert>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>閉じる</Button>
          <Button
            onClick={handleOpenEditDialog}
            variant="contained"
            startIcon={<Edit />}
            disabled={isLoading || !!error}
          >
            編集
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編集ダイアログ */}
      {templates && (
        <EmailTemplateEditDialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          companyId={companyId}
          templates={templates}
        />
      )}
    </>
  );
}
